import { emitErrorNotification } from './notificationBus';

const isNetworkFailure = (errorMessage = '', errorName = '') => {
  if (errorName === 'TypeError') {
    return true;
  }
  return /network|load failed|failed to fetch|networkerror/i.test(errorMessage);
};

const getErrorMessageFromResponse = async (response, fallbackMessage) => {
  try {
    const responseContentType = response.headers.get('content-type') || '';
    if (responseContentType.includes('application/json')) {
      const data = await response.json();
      if (data?.error) return data.error;
      if (data?.message) return data.message;
    } else {
      const text = await response.text();
      if (text?.trim()) return text;
    }
  } catch (_error) {
    // Ignore parse failure and continue to status-based fallback.
  }

  if (response.status === 401) return 'Your session expired. Please sign in again.';
  if (response.status === 403) return 'You do not have permission to perform this action.';
  if (response.status === 404) return 'The requested resource was not found.';
  if (response.status >= 500) return 'Server error while processing your request. Please try again.';
  return fallbackMessage;
};

// Auth is httpOnly-cookie based (see AuthContext/api.js). localStorage never
// holds a token in the current scheme, so this is only a defensive fallback
// for any legacy caller that hasn't been migrated yet — it must never
// synthesize a bogus "Bearer null" header when the lookup comes back empty.
const getAuthToken = () => {
  if (typeof window === 'undefined') return null;
  try {
    const token = window.localStorage.getItem('authToken') || window.localStorage.getItem('token');
    return token && token !== 'null' && token !== 'undefined' ? token : null;
  } catch (_error) {
    return null;
  }
};

const buildRequestHeaders = (providedHeaders) => {
  // Normalize caller-provided headers into a plain object while preserving casing of keys
  // they explicitly supplied (so Authorization passed in by the caller always wins).
  const headers = {};
  if (providedHeaders) {
    if (providedHeaders instanceof Headers) {
      providedHeaders.forEach((value, key) => { headers[key] = value; });
    } else if (Array.isArray(providedHeaders)) {
      providedHeaders.forEach(([key, value]) => { headers[key] = value; });
    } else {
      Object.assign(headers, providedHeaders);
    }
  }

  // Strip out any bogus "Bearer null"/"Bearer undefined" header a caller may
  // have supplied from a stale localStorage lookup — sending it would make
  // the server reject the request even though the valid httpOnly auth cookie
  // is present and would otherwise authenticate the request.
  Object.keys(headers).forEach((key) => {
    if (key.toLowerCase() === 'authorization') {
      const value = String(headers[key] || '');
      if (/^Bearer\s+(null|undefined)?$/i.test(value.trim())) {
        delete headers[key];
      }
    }
  });

  const hasAuthHeader = Object.keys(headers).some((k) => k.toLowerCase() === 'authorization');
  if (!hasAuthHeader) {
    const token = getAuthToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  return headers;
};

export const apiFetch = async (
  url,
  options = {},
  { fallbackMessage = 'Request failed', suppressGlobalError = false } = {}
) => {
  try {
    const response = await fetch(url, {
      ...options,
      // Ensure the httpOnly auth cookie is always sent, regardless of how
      // REACT_APP_API_URL is configured in a given deployment.
      credentials: options.credentials || 'include',
      headers: buildRequestHeaders(options.headers),
    });
    if (!response.ok) {
      const message = await getErrorMessageFromResponse(response, fallbackMessage);
      throw new Error(message);
    }

    if (response.status === 204) {
      return {};
    }

    const responseContentType = response.headers.get('content-type') || '';
    if (!responseContentType.includes('application/json')) {
      return {};
    }

    return await response.json();
  } catch (error) {
    const message = isNetworkFailure(error?.message || '', error?.name || '')
      ? 'Network connection was lost. Please check your connection and try again.'
      : (error?.message || fallbackMessage);

    if (!suppressGlobalError) {
      emitErrorNotification(message);
    }

    throw new Error(message);
  }
};
